import { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import { IntuiAvatar } from '@/components/intueri/IntuiAvatar'
import { IntuiBadge } from '@/components/intueri/IntuiBadge'
import { Input } from '@/components/ui/input'
import type { ShareCandidate } from '@/types/shares'

/** A chosen recipient — either a resolved org member or a raw typed address. */
export interface SelectedRecipient {
  email: string
  name: string | null
  picture: string | null
  /** True when the address matched nobody in the directory. */
  isNewToOrg: boolean
}

interface ShareRecipientPickerProps {
  selected: SelectedRecipient[]
  onChange: (next: SelectedRecipient[]) => void
  /** Addresses already granted access — hidden from results to avoid re-adding. */
  excludeEmails?: string[]
  organizationName?: string
  disabled?: boolean
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DEBOUNCE_MS = 300

/**
 * FS-663 recipient picker.
 *
 * Resolves against `GET /v1/organization/users/search` — the same Keycloak-backed
 * directory the @-mention autocomplete uses, so there is no second source of
 * truth for "who is in this org". Search results are component state, not store
 * state: they are per-keystroke and ephemeral.
 */
export function ShareRecipientPicker({
  selected,
  onChange,
  excludeEmails = [],
  organizationName,
  disabled,
}: ShareRecipientPickerProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ShareCandidate[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [open, setOpen] = useState(false)

  // Guards against a slow earlier request overwriting a newer one's results.
  const requestSeq = useRef(0)

  const excluded = new Set(
    [...excludeEmails, ...selected.map((s) => s.email)].map((e) => e.toLowerCase())
  )

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length === 0) {
      setResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    const seq = ++requestSeq.current
    const timer = setTimeout(async () => {
      try {
        const users = await apiService.searchOrganizationUsers(trimmed)
        if (seq !== requestSeq.current) return
        // Store RAW results. Excluding here would bake in the chip set as it was
        // when the request started, so a recipient added while the search was in
        // flight would still show up as selectable.
        setResults(
          users
            .filter((u) => Boolean(u.email))
            .map((u) => ({
              user_id: u.user_id,
              email: u.email,
              name: u.name,
              picture: u.picture,
            }))
        )
      } catch {
        if (seq === requestSeq.current) setResults([])
      } finally {
        if (seq === requestSeq.current) setIsSearching(false)
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [query])

  // Exclusion applied at RENDER time, against the CURRENT chip set — see the
  // note in the fetch above.
  const visibleResults = results.filter(
    (r) => r.email && !excluded.has(r.email.toLowerCase())
  )

  const add = useCallback(
    (recipient: SelectedRecipient) => {
      onChange([...selected, recipient])
      setQuery('')
      setResults([])
      setOpen(false)
    },
    [onChange, selected]
  )

  const remove = useCallback(
    (email: string) => onChange(selected.filter((s) => s.email !== email)),
    [onChange, selected]
  )

  const trimmed = query.trim()
  const isEmail = EMAIL_RE.test(trimmed)
  // Offer "invite this address" only when it is a real address that matched
  // nobody — otherwise a half-typed name would look like an invite option.
  const canInviteRaw =
    isEmail && !isSearching && visibleResults.length === 0 && !excluded.has(trimmed.toLowerCase())

  return (
    <div className="flex flex-col gap-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((r) => (
            <IntuiBadge
              key={r.email}
              variant={r.isNewToOrg ? 'warning' : 'secondary'}
              className="pr-1"
            >
              <span className="max-w-[220px] truncate">{r.name || r.email}</span>
              <button
                type="button"
                onClick={() => remove(r.email)}
                disabled={disabled}
                aria-label={t('common:share_modal.remove_recipient', {
                  name: r.name || r.email,
                })}
                className="ml-1 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
              >
                <X className="h-3 w-3" />
              </button>
            </IntuiBadge>
          ))}
        </div>
      )}

      <div className="relative">
        <Input
          type="text"
          value={query}
          disabled={disabled}
          placeholder={t('common:share_modal.recipient_placeholder')}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canInviteRaw) {
              e.preventDefault()
              add({ email: trimmed.toLowerCase(), name: null, picture: null, isNewToOrg: true })
            }
          }}
        />

        {open && trimmed.length > 0 && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-md">
            {isSearching && (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {t('common:share_modal.searching')}
              </p>
            )}

            {!isSearching &&
              visibleResults.map((r) => (
                <button
                  key={r.user_id}
                  type="button"
                  onClick={() =>
                    add({
                      email: (r.email ?? '').toLowerCase(),
                      name: r.name,
                      picture: r.picture,
                      isNewToOrg: false,
                    })
                  }
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent"
                >
                  <IntuiAvatar
                    size="sm"
                    {...(r.picture ? { src: r.picture } : {})}
                    fallback={(r.name || r.email || '?').slice(0, 2).toUpperCase()}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{r.name || r.email}</span>
                    {r.name && r.email && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {r.email}
                      </span>
                    )}
                  </span>
                </button>
              ))}

            {!isSearching && visibleResults.length === 0 && !canInviteRaw && (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {t('common:share_modal.no_matches', { query: trimmed })}
              </p>
            )}

            {canInviteRaw && (
              <button
                type="button"
                onClick={() =>
                  add({
                    email: trimmed.toLowerCase(),
                    name: null,
                    picture: null,
                    isNewToOrg: true,
                  })
                }
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-accent"
              >
                <span className="text-sm">
                  {t('common:share_modal.invite_new', { email: trimmed })}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('common:share_modal.invite_new_hint', {
                    organization: organizationName || 'your organization',
                  })}
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ShareRecipientPicker
